import { Formik } from "formik";
import * as _ from "lodash";
import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { components } from "react-select";
import { UncontrolledTooltip } from "reactstrap";
import Skeleton from "react-loading-skeleton";
import 'react-loading-skeleton/dist/skeleton.css';
import BaseLayoutWithCard from "../../../../base/components/BaseLayoutWithCard";
import Button from "../../../../base/components/Button";
import { BUTTON_COLORS } from "../../../../base/components/Button/appearance";
import { DropZoneCard } from "../../../../base/components/Dropzone";
import useUploadImages from "../../../../base/components/Dropzone/useUploadImages";
import FormikColorPicker from "../../../../base/components/FormikColorPicker";
import FormikReactSelect from "../../../../base/components/FormikReactSelect";
import Icon from "../../../../base/components/Icon";
import MobilePreview from "../../../../base/components/MobilePreview/MobilePreview";
import {
  ALLOWED_LOGO_FILE_EXTENSION_EXTENDED,
  ASSETS_FILE_TYPE,
  BYTES_IN_KILOBYTE,
  ERROR_ALLOWED_LOGO_FILE_TYPE_EXTENDED_MESSAGE,
  ERROR_ALLOWED_LOGO_FILE_TYPE_MESSAGE,
  ERROR_LOGO_FILE_SIZE_MESSAGE,
  KILOBYTES_IN_MEGABYTE,
  LOGO_FILE_MASK_EXTENDED,
  MAX_LOGO_FILE_SIZE
} from "../../../../base/constants/shared";
import {KEY_USER} from "../../../../base/constants/storage";
import FormikInput from "../../../../base/components/FormikInput";
import joinClassNames from "../../../../base/helpers/joinClassNames";
import { useService } from "../../../../base/hooks/useService";
import { getAdminRolePermissions, isPermissionVisible, isServiceEditable } from "../../../../base/helpers/adminRolePermissions";
import ConfigService from "../../../../services/ConfigService";
import ToasterService from "../../../../services/ToastService";
import StorageService from "../../../../services/StorageService";
import { validateEmptyFile, validateFileSize, validateFileType } from "../../../../validation/fileUploadAndProcessing";
import { checkOnDefined } from "../../../app/Biomarkers/CreateEdit/fromValidation";
import Title from "../../Biomarkers/Details/Title";
import { SortableList } from "../components/SortableList";
import MobilePreviewCarousel from "../MobileAppPreview/MobilePreviewCarousel/MobilePreviewCarousel";
import { initialValues as initialValuesStatic, validationSchema } from "./form";
import NutritionalDashboard from "../MobileAppPreview/NutritionalDashboard.template";
import SplashScreen from "../MobileAppPreview/SplashScreen/SplashScreen.template";
import SignInUpScreen from "../MobileAppPreview/SignInUpScreen/SignInUpScreen.template";
import { MOBILE_SERVICE_TYPES, SERVICE_TO_WIDGET_AND_FEATURE, fontOptions, fontClassMap, SECTION, SECTION_TO_SLIDE_INDEX, SERVICE_FEATURE_NAME } from "../constants";

const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

export function MobileAppCustomisationForm() {
  const breadcrumbs = {
    title: "Mobile App Customisation", breadcrumbItems: []
  };

  const splashRef = useRef(null);
  const signInRef = useRef(null);
  const dashboardRef = useRef(null);
  const widgetsRef = useRef(null);
  const featuresRef = useRef(null);

  /**
   * @type {StorageService}
   */
  const storage = useService(StorageService);
  const DEFAULT_OPTIMALLY_ME_ORG = 1;
  const { organisationId } = storage.get(KEY_USER, { organisationId: DEFAULT_OPTIMALLY_ME_ORG });
  
  /**
   * @type {ConfigService}
   */
  const configService = useService(ConfigService);
  /**
   * @type {ToasterService}
   */
  const toastService = useService(ToasterService);

  const [initialValues, setInitialValues] = useState(initialValuesStatic);
  const [filteredMobileServices, setMobileServices] = useState([]);
  const [filteredMobileWidgetService, setMobileWidgetServices] = useState([]);
  const [isLoading, updateIsLoading] = useState(true);
  const [isSubmitting, updateIsSubmitting] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedSignUpLogoFile, setSelectedSignUpLogoFile] = useState(null);
  const [selectedSplashScreenBgFile, setSelectedSplashScreenBgFile] = useState(null);
  const [selectedSignUpBgFile, setSelectedSignUpBgFile] = useState(null);
  const [apiSettings, setApiSettings] = useState({});
  const [apiConfig, setApiConfig] = useState({});
  const [formValues, setFormValues] = useState({});
  const [isUploading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [activeSlide, setActiveSlide] = useState(0);
  const uploadImage = useUploadImages();

  const isAppCustomisationEditable = isServiceEditable("App Customisation");
  const formDisabled = !isAppCustomisationEditable;
  const rolePermissions = getAdminRolePermissions();
  const roleVisibilityKey = rolePermissions
    .map((permission) => `${permission.organisationServiceName}:${!!permission.isVisible}`)
    .join("|");

  useEffect(() => {
    Promise.all([
      configService.fetchCustomerConfig(),
      configService.fetchThemeSettings(),
      configService.fetchMobileServices(),
      configService.fetchServiceSettings(organisationId, { isEnabled: true })
    ]).then(([rawConfig, themeSettingsRaw, mobileServices, enabledServices]) => {
      const themeFields = ["id", "propertyName", "fileId", "value", "file", "page", "themeType"];
      const configFields = ["id", "variable", "value"];
      const themeSettings = _.chain(themeSettingsRaw)
        .get("data")
        .map(item => _.pick(item, themeFields))
        .keyBy("propertyName")
        .value();

      const config = _.chain(rawConfig)
        .get("data")
        .map(item => _.pick(item, configFields))
        .keyBy("variable")
        .value();

      setApiConfig(config);
      setApiSettings(themeSettings);
      setSelectedFile({
        ...themeSettings.organisationLogoMobile
      });
      setSelectedSignUpLogoFile({
        ...themeSettings.mobileSignUpLogo
      });
      setSelectedSplashScreenBgFile({
        ...themeSettings.mobileSplashScreenBackground
      });
      setSelectedSignUpBgFile({
        ...themeSettings.mobileSignUpBackground
      });
      updateIsLoading(false);

      setInitialValues((prev) => ({
        ...prev,
        organisationLogoMobile: themeSettings.organisationLogoMobile.value,
        primaryColorMobile: themeSettings.primaryColorMobile.value,
        secondaryColorMobile: themeSettings.secondaryColorMobile.value,
        organisationLogoMobileWidth: themeSettings.organisationLogoMobileWidth.value,
        organisationLogoMobileHeight: themeSettings.organisationLogoMobileHeight.value,
        mobileSplashScreenBackground: themeSettings.mobileSplashScreenBackground.value,
        mobileSignUpLogo: themeSettings.mobileSignUpLogo.value,
        mobileSignUpBackground: themeSettings.mobileSignUpBackground.value,
        CONTACT_US_WEBSITE: config.CONTACT_US_WEBSITE.value,
        CONTACT_US_EMAIL: config.CONTACT_US_EMAIL.value,
        CONTACT_US_LINKEDIN: config.CONTACT_US_LINKEDIN.value,
        CONTACT_US_FB: config.CONTACT_US_FB.value,
        CONTACT_US_INSTAGRAM: config.CONTACT_US_INSTAGRAM.value,
        CONTACT_US_TIKTOK: config.CONTACT_US_TIKTOK.value,
        CONTACT_US_TWITTER: config.CONTACT_US_TWITTER.value,
        mobileSignUpLogoHeight: themeSettings.mobileSignUpLogoHeight.value,
        mobileSignUpLogoWidth: themeSettings.mobileSignUpLogoWidth.value,
        mobileFont: themeSettings.mobileFont.value
      }));

      const enabledServiceNames = new Set((enabledServices && enabledServices.data || []).map(s => s.name));

      const widgetFeatureToService = {};
      Object.entries(SERVICE_TO_WIDGET_AND_FEATURE).forEach(([service, widgets]) => {
        widgets.forEach(widget => {
          widgetFeatureToService[widget] = service;
        });
      });

      const filteredMobileServices = (mobileServices.data || []).filter((service) => {
        const mappedService = widgetFeatureToService[service.name];
        if (!mappedService) {
          return true;
        }
        return enabledServiceNames.has(mappedService) && isPermissionVisible(rolePermissions, mappedService);
      });

      setMobileServices(filteredMobileServices?.filter((service) => service.type === MOBILE_SERVICE_TYPES.features));
      setMobileWidgetServices(filteredMobileServices?.filter((service) => service.type === MOBILE_SERVICE_TYPES.widgets))
    })
  }, [configService, roleVisibilityKey]);

  const activeMobileServices = useMemo(
      () =>
          filteredMobileServices
              .filter(({ isEnabled }) => isEnabled)
              .map(({ name, ...service }) => ({
                ...service,
                name: name.split(" ")[0],
              })),
      [filteredMobileServices],
  )

  const activeMobileWidgetService = useMemo(
      () =>
          filteredMobileWidgetService
              .filter(({ isEnabled }) => isEnabled),
      [filteredMobileWidgetService],
  )

  const handleAcceptedFile = useCallback(async (file, key) => {
    setErrorMessage("");

    try {
      validateFileType(file, ALLOWED_LOGO_FILE_EXTENSION_EXTENDED, ERROR_ALLOWED_LOGO_FILE_TYPE_EXTENDED_MESSAGE);
      validateFileSize(file, MAX_LOGO_FILE_SIZE, ERROR_LOGO_FILE_SIZE_MESSAGE);
      validateEmptyFile(file);
    } catch ({ message }) {
      toastService.error(message)
      setErrorMessage(message);
      return;
    }

    const fileObj = {
      value: URL.createObjectURL(file),
      fileData: file,
      file: {
        name: file.name,
        bytes: file.size
      }
    };

    if (key === "organisationLogoMobile") setSelectedFile(fileObj);
    if (key === "mobileSignUpLogo") setSelectedSignUpLogoFile(fileObj);
    if (key === "mobileSplashScreenBackground") setSelectedSplashScreenBgFile(fileObj);
    if (key === "mobileSignUpBackground") setSelectedSignUpBgFile(fileObj);

  }, [setErrorMessage, toastService]);

  const onDrop = useCallback(
    (acceptedFiles, fileRejections, key) => {
      fileRejections.forEach((file) => {
        file.errors.forEach((err) => {
          if (err.code === ERROR_ALLOWED_LOGO_FILE_TYPE_EXTENDED_MESSAGE) {
            toastService.error(ERROR_ALLOWED_LOGO_FILE_TYPE_MESSAGE);
          }
        });
      });

      if (!acceptedFiles.length) return;

      acceptedFiles.forEach((file) => handleAcceptedFile(file, key));

    },
    [handleAcceptedFile]
  );

  const deleteLogo = () => {
    setSelectedFile(null);
  }

  const deleteSignUpLogo = () => {
    setSelectedSignUpLogoFile(null);
  }

  const deleteSplashScreenBackground = () => {
    setSelectedSplashScreenBgFile(null);
  }

  const deleteSignUpBackground = () => {
    setSelectedSignUpBgFile(null);
  }

  const submitSettings = async (values) => {
    updateIsSubmitting(true);
    try {
      const configToSubmit = _.pick(apiConfig, _.keys(values));
      const settingsToSubmit = _.pick(apiSettings, _.keys(values));
      _.map(settingsToSubmit, (setting) => {
        setting.value = values[setting.propertyName];
        return setting;
      });
      _.map(configToSubmit, (config) => {
        config.value = values[config.variable] ?? '';
      });

      if (!selectedFile.id) {
        const uploadedFile = await uploadImage(selectedFile.fileData, ASSETS_FILE_TYPE); // uploadedFile.file.id
        settingsToSubmit.organisationLogoMobile = {
          ...settingsToSubmit.organisationLogoMobile,
          fileId: uploadedFile.file.id
        };
        setSelectedFile((prev) => ({
          ...prev,
          id: uploadedFile.file.id
        }));
      }

      if (!selectedSignUpLogoFile.id) {
        const uploadedFile = await uploadImage(selectedSignUpLogoFile.fileData, ASSETS_FILE_TYPE); // uploadedFile.file.id
        settingsToSubmit.mobileSignUpLogo = {
          ...settingsToSubmit.mobileSignUpLogo,
          fileId: uploadedFile.file.id
        };
        setSelectedSignUpLogoFile((prev) => ({
          ...prev,
          id: uploadedFile.file.id
        }));
      }

      if (!selectedSplashScreenBgFile.id) {
        const uploadedFile = await uploadImage(selectedSplashScreenBgFile.fileData, ASSETS_FILE_TYPE); // uploadedFile.file.id
        settingsToSubmit.mobileSplashScreenBackground = {
          ...settingsToSubmit.mobileSplashScreenBackground,
          fileId: uploadedFile.file.id
        };
        setSelectedSplashScreenBgFile((prev) => ({
          ...prev,
          id: uploadedFile.file.id
        }));
      }

      if (!selectedSignUpBgFile.id) {
        const uploadedFile = await uploadImage(selectedSignUpBgFile.fileData, ASSETS_FILE_TYPE); // uploadedFile.file.id
        settingsToSubmit.mobileSignUpBackground = {
          ...settingsToSubmit.mobileSignUpBackground,
          fileId: uploadedFile.file.id
        };
        setSelectedSignUpBgFile((prev) => ({
          ...prev,
          id: uploadedFile.file.id
        }));
      }

      settingsToSubmit.organisationLogoMobile = _.omit(settingsToSubmit.organisationLogoMobile, ['value', 'file']);
      settingsToSubmit.mobileSplashScreenBackground = _.omit(settingsToSubmit.mobileSplashScreenBackground, ['value', 'file']);
      settingsToSubmit.mobileSignUpLogo = _.omit(settingsToSubmit.mobileSignUpLogo, ['value', 'file']);
      settingsToSubmit.mobileSignUpBackground = _.omit(settingsToSubmit.mobileSignUpBackground, ['value', 'file']);
      const updatedConfig = await configService.updateCustomerConfig(_.values(configToSubmit));
      const preparedMobileWidgetServices = _.map(filteredMobileWidgetService, (mobileService) => _.pick(mobileService, ["id", "isEnabled", "order"]));

      let preparedMobileServices = _.map(filteredMobileServices, (mobileService) =>
        _.pick(mobileService, ["id", "isEnabled", "order", "name"])
      );

      preparedMobileServices.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

      const MIRROR_MIRROR = SERVICE_FEATURE_NAME.mirrorMirror;
      let nextOrder = 1;
      let seenOrders = new Set();
      let mirrorMirrorIdx = preparedMobileServices.findIndex(s => s.name === MIRROR_MIRROR);
      let mirrorMirror = mirrorMirrorIdx !== -1 ? preparedMobileServices[mirrorMirrorIdx] : null;

      if (mirrorMirror && mirrorMirror.isEnabled) {
        mirrorMirror.order = 1;
        seenOrders.add(1);
      }

      for (let i = 0; i < preparedMobileServices.length; i++) {
        const s = preparedMobileServices[i];
        if (mirrorMirror && mirrorMirror.isEnabled && i === mirrorMirrorIdx) continue;
        while (seenOrders.has(nextOrder)) nextOrder++;
        s.order = nextOrder;
        seenOrders.add(nextOrder);
        nextOrder++;
      }

      preparedMobileServices = preparedMobileServices.map(({name, ...rest}) => rest);

      await Promise.all([
        configService.updateThemeSettings(_.values(settingsToSubmit)),
        configService.updateMobileServices(MOBILE_SERVICE_TYPES.features, preparedMobileServices),
        configService.updateMobileServices(MOBILE_SERVICE_TYPES.widgets, preparedMobileWidgetServices)
      ]);
      toastService.success("Settings was successfully uploaded");
    } catch (err) {
      console.error(err);
      toastService.error(err)
      setErrorMessage(err);
    } finally {
      updateIsSubmitting(false);
    }
  };

  const onSortEnd = ({ oldIndex, newIndex }) => {
    setMobileServices((prev) => {
      const MIRROR_MIRROR = SERVICE_FEATURE_NAME.mirrorMirror;
      const mirrorMirror = prev.find(s => s.name === MIRROR_MIRROR);
      const rest = prev.filter(s => s.name !== MIRROR_MIRROR);
      const reordered = reorder(rest, oldIndex, newIndex).map(
        ({ order, ...restItem }, index) => ({
          ...restItem,
          order: checkOnDefined(order) ? index + 1 : undefined
        })
      );
      if (mirrorMirror) {
        return mirrorMirror.isEnabled ? [mirrorMirror, ...reordered] : [mirrorMirror, ...reordered];
      }
      return reordered;
    });
  };

  const toggleService = (id) => {
    setMobileServices((prev) => {
      const MIRROR_MIRROR = SERVICE_FEATURE_NAME.mirrorMirror;
      const mirrorMirror = prev.find(s => s.name === MIRROR_MIRROR);
      const rest = prev.filter(s => s.name !== MIRROR_MIRROR);
      if (mirrorMirror && mirrorMirror.id === id) {
        const toggledMM = { ...mirrorMirror, isEnabled: !mirrorMirror.isEnabled };
        return [toggledMM, ...rest];
      }
      const updated = rest.map((item) =>
        item.id === id ? { ...item, isEnabled: !item.isEnabled } : item
      );
      return mirrorMirror ? [mirrorMirror, ...updated] : updated;
    });
  }

  const onWidgetSortEnd = ({ oldIndex, newIndex }) => {
    setMobileWidgetServices((prev) => (
        reorder(prev, oldIndex, newIndex).map(
            ({ order, ...rest }, index) => ({
              ...rest,
              order: checkOnDefined(order) ? index + 1 : undefined
            })
        )
    ));
  };

  const toggleWidgetService = (id) => {
    setMobileWidgetServices((prev) => (
        prev.map((item) => {
          if (item.id === id) {
            return {
              ...item,
              isEnabled: !item.isEnabled
            }
          }
          return item;
        })
    ));
  }

  const CustomSingleValue = (props) => {
    const font = props.data.value;
    return (
      <components.SingleValue {...props}>
        <span className={fontClassMap[font]}>
          {props.data.label}
        </span>
      </components.SingleValue>
    );
  };

  const CustomOption = (props) => {
    const font = props.data.value;
    return (
      <components.Option {...props}>
        <span className={fontClassMap[font]}>
          {props.data.label}
        </span>
      </components.Option>
    );
  };

  const handleSectionFocus = (section) => {
    setActiveSlide(SECTION_TO_SLIDE_INDEX[section]);
  };

  const mobileSlides = [
    {
      id: "slide-1",
      content: (
        <section className="app_customisation_font_family section-splash-screen">
          <p className="mb-0">Splash screen</p>
          <MobilePreview isSplashAndSignInUpPage>
            <SplashScreen
              background={formValues.mobileSplashScreenBackground}
              logo={formValues.organisationLogoMobile}
              logoWidth={formValues.organisationLogoMobileWidth}
              logoHeight={formValues.organisationLogoMobileHeight} />
          </MobilePreview>   
        </section>  
      ),
    },
    {
      id: "slide-2",
      content: (
        <section className="app_customisation_font_family section-sign-in-up-screen">
          <p className="mb-0">Sign In/Up screen</p>
          <MobilePreview isSplashAndSignInUpPage>
            <SignInUpScreen
              background={formValues.mobileSignUpBackground}
              logo={formValues.mobileSignUpLogo}
              logoWidth={formValues.mobileSignUpLogoWidth}
              logoHeight={formValues.mobileSignUpLogoHeight}
              fontFamily={fontClassMap[formValues.mobileFont]}
              primaryColorMobile={formValues.primaryColorMobile} />
          </MobilePreview>
        </section>
      ),
    },
    {
      id: "slide-3",
      content: (
        <section className="app_customisation_font_family section-lifestyle-dashboard">
          <p className="mb-0">Lifestyle dashboard</p>
          <MobilePreview isDashboardPage services={activeMobileServices}>
            <NutritionalDashboard
              primaryColor={formValues.primaryColorMobile}
              secondaryColor={formValues.secondaryColorMobile}
              widgets={activeMobileWidgetService}
              hasTopOffset
            />
          </MobilePreview>
        </section>
      ),
    },
  ];

  return <BaseLayoutWithCard breadcrumbs={breadcrumbs}>
    <div className="w-100">
      <div className="d-flex pt-3 justify-content-end">

      </div>
      <div className="d-flex gap-5">
        <section className="w-50 p-3 pe-4 border-end border-1" style={{ minWidth: "50%" }}>

          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            validateOnBlur
            onSubmit={submitSettings}
            enableReinitialize
          >
            {({ errors, handleSubmit, values, setFieldValue }) => {
              setFormValues(values)
              return(
                  <form className={joinClassNames("form-horizontal", isSubmitting && "pointer-events-none")}
                        onSubmit={handleSubmit}>
                    <label ref={splashRef} htmlFor="">Splash Screen</label>
                    <div
                      className="ps-3"
                      onFocus={() => handleSectionFocus(SECTION.SPLASH)}>
                      <label htmlFor="">Logo</label>
                      {isLoading &&
                      <Skeleton height={93} />
                      }
                      {!selectedFile && !isLoading &&
                      <DropZoneCard
                          onDrop={(files, rejections) => onDrop(files, rejections, "organisationLogoMobile")}
                          errorMessage={errorMessage}
                          isDropContainer={true}
                          className={""}
                          fileMask={LOGO_FILE_MASK_EXTENDED}
                          disabled={formDisabled}
                      >
                        <section className="upload-container--section">
                          <p className="upload-container--section__cloud-upload-desc">Upload logo</p>
                          <p className="upload-container--section__desc mb-0">Drop a PNG, JPG or JPEG file.</p>
                          <p
                              className="upload-container--section__desc">Max {MAX_LOGO_FILE_SIZE / BYTES_IN_KILOBYTE / KILOBYTES_IN_MEGABYTE} Mb</p>
                        </section>
                      </DropZoneCard>
                      }
                      {selectedFile && !isLoading && !isUploading &&
                      <div>
                        <div
                            className="d-flex align-items-center justify-content-between p-4 selected-file-container formik-file-input">
                          <div className="d-flex align-items-center">
                            <img src={selectedFile.value} alt="Logo" className="formik-file-input-logo" />
                            <div className="d-flex flex-column justify-content-between px-4">
                            <span
                                className="selected-file-container__selected-file-name mb-1">{selectedFile.file.name}</span>
                              <span className="font-semibold">
                              {(selectedFile.file.bytes / BYTES_IN_KILOBYTE).toFixed(2)} KB
                            </span>
                            </div>
                          </div>
                          {!formDisabled &&
                            <Icon icon="trashIcon" className="cursor-pointer ms-3" onClick={deleteLogo} />
                          }
                        </div>
                      </div>
                      }

                      <div className="mt-3">
                        <label className="d-flex align-items-center mb-1" htmlFor="">
                          Height & Width Inputs
                          <Icon icon="infoCircle" className="ms-2" id={`height-width-inputs-info`}/>
                          <UncontrolledTooltip
                            popperClassName={joinClassNames(
                              "tooltip-alternative-name error-result-tooltip"
                            )}
                            innerClassName="pre-line text-truncate error-inner-max-height text-start"
                            placement="bottom"
                            target={`height-width-inputs-info`}
                          >
                            Optimal dimensions for best display on all screens.
                          </UncontrolledTooltip>
                          </label>
                        {isLoading ?
                          <Skeleton height={65.5} />
                          :
                          <div className="d-flex justify-content-between">
                            <div className="w-50 me-2">
                              <FormikInput
                                name="organisationLogoMobileWidth"
                                label="Logo width"
                                type="number"
                                min={120}
                                max={160}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="w-50 ms-2">
                              <FormikInput
                                name="organisationLogoMobileHeight"
                                label="Logo height"
                                type="number"
                                min={32}
                                max={48}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                        }
                      </div>

                      <div className="mt-3">
                        <label htmlFor="">Background</label>
                        {isLoading &&
                        <Skeleton height={93} />
                        }
                        {!selectedSplashScreenBgFile && !isLoading &&
                        <DropZoneCard
                            onDrop={(files, rejections) => onDrop(files, rejections, "mobileSplashScreenBackground")}
                            errorMessage={errorMessage}
                            isDropContainer={true}
                            className={""}
                            fileMask={LOGO_FILE_MASK_EXTENDED}
                            disabled={formDisabled}
                        >
                          <section className="upload-container--section">
                            <p className="upload-container--section__cloud-upload-desc">Upload background</p>
                            <p className="upload-container--section__desc mb-0">Drop a PNG, JPG or JPEG file.</p>
                            <p
                                className="upload-container--section__desc">Max {MAX_LOGO_FILE_SIZE / BYTES_IN_KILOBYTE / KILOBYTES_IN_MEGABYTE} Mb</p>
                          </section>
                        </DropZoneCard>
                        }
                        {selectedSplashScreenBgFile && !isLoading && !isUploading &&
                        <div>
                          <div
                              className="d-flex align-items-center justify-content-between p-4 selected-file-container formik-file-input">
                            <div className="d-flex align-items-center">
                              <img src={selectedSplashScreenBgFile.value} alt="Logo" className="formik-file-input-logo" />
                              <div className="d-flex flex-column justify-content-between px-4">
                              <span
                                  className="selected-file-container__selected-file-name mb-1">{selectedSplashScreenBgFile.file.name}</span>
                                <span className="font-semibold">
                                {(selectedSplashScreenBgFile.file.bytes / BYTES_IN_KILOBYTE).toFixed(2)} KB
                              </span>
                              </div>
                            </div>
                            {!formDisabled &&
                              <Icon icon="trashIcon" className="cursor-pointer ms-3" onClick={deleteSplashScreenBackground} />
                            }
                          </div>
                        </div>
                        }
                      </div>
                    </div>
                    <label ref={signInRef} className="mt-4 pt-2" htmlFor="">Sign In/Up Screen</label>
                    <div
                      className="ps-3"
                      onFocus={() => handleSectionFocus(SECTION.SIGNIN)}>
                      <label htmlFor="">Logo</label>
                      {isLoading &&
                      <Skeleton height={93} />
                      }
                      {!selectedSignUpLogoFile && !isLoading &&
                      <DropZoneCard
                          onDrop={(files, rejections) => onDrop(files, rejections, "mobileSignUpLogo")}
                          errorMessage={errorMessage}
                          isDropContainer={true}
                          className={""}
                          fileMask={LOGO_FILE_MASK_EXTENDED}
                          disabled={formDisabled}
                      >
                        <section className="upload-container--section">
                          <p className="upload-container--section__cloud-upload-desc">Upload logo</p>
                          <p className="upload-container--section__desc mb-0">Drop a PNG, JPG or JPEG file.</p>
                          <p
                              className="upload-container--section__desc">Max {MAX_LOGO_FILE_SIZE / BYTES_IN_KILOBYTE / KILOBYTES_IN_MEGABYTE} Mb</p>
                        </section>
                      </DropZoneCard>
                      }
                      {selectedSignUpLogoFile && !isLoading && !isUploading &&
                        <div>
                          <div
                              className="d-flex align-items-center justify-content-between p-4 selected-file-container formik-file-input">
                            <div className="d-flex align-items-center">
                              <img src={selectedSignUpLogoFile.value} alt="Logo" className="formik-file-input-logo" />
                              <div className="d-flex flex-column justify-content-between px-4">
                              <span
                                  className="selected-file-container__selected-file-name mb-1">{selectedSignUpLogoFile.file.name}</span>
                                <span className="font-semibold">
                                {(selectedSignUpLogoFile.file.bytes / BYTES_IN_KILOBYTE).toFixed(2)} KB
                              </span>
                              </div>
                            </div>
                          {!formDisabled &&
                            <Icon icon="trashIcon" className="cursor-pointer ms-3" onClick={deleteSignUpLogo} />
                          }
                          </div>
                        </div>
                      }

                      <div className="mt-3">
                        <label className="d-flex align-items-center mb-1" htmlFor="">
                          Height & Width Inputs
                          <Icon icon="infoCircle" className="ms-2" id={`height-width-inputs-info`}/>
                          <UncontrolledTooltip
                            popperClassName={joinClassNames(
                              "tooltip-alternative-name error-result-tooltip"
                            )}
                            innerClassName="pre-line text-truncate error-inner-max-height text-start"
                            placement="bottom"
                            target={`height-width-inputs-info`}
                          >
                            Optimal dimensions for best display on all screens.
                          </UncontrolledTooltip>
                          </label>
                        {isLoading ?
                          <Skeleton height={65.5} />
                          :
                          <div className="d-flex justify-content-between">
                            <div className="w-50 me-2">
                              <FormikInput
                                name="mobileSignUpLogoWidth"
                                label="Logo width"
                                type="number"
                                min={30}
                                max={48}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="w-50 ms-2">
                              <FormikInput
                                name="mobileSignUpLogoHeight"
                                label="Logo height"
                                type="number"
                                min={30}
                                max={48}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                        }
                      </div>

                      <div className="mt-3">
                        <label htmlFor="">Background</label>
                        {isLoading &&
                        <Skeleton height={93} />
                        }
                        {!selectedSignUpBgFile && !isLoading &&
                        <DropZoneCard
                            onDrop={(files, rejections) => onDrop(files, rejections, "mobileSignUpBackground")}
                            errorMessage={errorMessage}
                            isDropContainer={true}
                            className={""}
                            fileMask={LOGO_FILE_MASK_EXTENDED}
                            disabled={formDisabled}
                        >
                          <section className="upload-container--section">
                            <p className="upload-container--section__cloud-upload-desc">Upload background</p>
                            <p className="upload-container--section__desc mb-0">Drop a PNG, JPG or JPEG file.</p>
                            <p
                                className="upload-container--section__desc">Max {MAX_LOGO_FILE_SIZE / BYTES_IN_KILOBYTE / KILOBYTES_IN_MEGABYTE} Mb</p>
                          </section>
                        </DropZoneCard>
                        }
                        {selectedSignUpBgFile && !isLoading && !isUploading &&
                        <div>
                          <div
                              className="d-flex align-items-center justify-content-between p-4 selected-file-container formik-file-input">
                            <div className="d-flex align-items-center">
                              <img src={selectedSignUpBgFile.value} alt="Logo" className="formik-file-input-logo" />
                              <div className="d-flex flex-column justify-content-between px-4">
                              <span
                                  className="selected-file-container__selected-file-name mb-1">{selectedSignUpBgFile.file.name}</span>
                                <span className="font-semibold">
                                {(selectedSignUpBgFile.file.bytes / BYTES_IN_KILOBYTE).toFixed(2)} KB
                              </span>
                              </div>
                            </div>
                          {!formDisabled &&
                            <Icon icon="trashIcon" className="cursor-pointer ms-3" onClick={deleteSignUpBackground} />
                          }
                          </div>
                        </div>
                        }
                      </div>
                    </div>
                    <label className="mt-4 pt-2" htmlFor="">Colors</label>
                    <div className="ps-3">
                      <FormikColorPicker
                          label="Primary color"
                          description="Primary color of buttons and important elements. This color will be displayed in all related elements on the mobile app."
                          placeholder=""
                          name="primaryColorMobile"
                          maxLength={255}
                          containerClassName="mb-4"
                          isLoading={isLoading}
                          disabled={formDisabled}
                      />
                      <FormikColorPicker
                          label="Secondary color"
                          placeholder=""
                          name="secondaryColorMobile"
                          maxLength={255}
                          containerClassName="mt-3 mb-4"
                          isLoading={isLoading}
                          disabled={formDisabled}
                      />
                    </div>
                    <label className="mt-2" htmlFor="">Font</label>
                    <div 
                      className="ps-3 app_customisation_font_family"
                      onFocus={() => handleSectionFocus(SECTION.FONTFAMILY)}>
                      {isLoading ? (
                        <Skeleton height={63.5} />
                      ) : (
                        <FormikReactSelect
                          setFieldValue={setFieldValue}
                          name="mobileFont"
                          label="Font Family"
                          placeholder="Select font"
                          value={fontOptions.find(({ value }) => value === values.mobileFont)}
                          options={fontOptions}
                          withError
                          containerClassName=""
                          className="custom-select"
                          isClearable={false}
                          additionalComponents={{
                            SingleValue: CustomSingleValue,
                            Option: CustomOption
                          }}
                          afterOnChange={(selectedFont) => {
                            // You can add logic here if needed, like:
                            console.log("Selected font:", selectedFont);
                          }}
                          disabled={formDisabled}
                        />
                      )}
                    </div>

                    <label className="mt-4 pt-1" htmlFor="">Contact Us Form</label>
                    <div className="ps-3">
                      {isLoading ?
                        <Skeleton height={355} />
                        :
                        <div className="mb-4">
                          <FormikInput
                            name="CONTACT_US_WEBSITE"
                            label="Website link"
                            disabled={formDisabled}
                          />
                          <div className="mt-3">
                            <FormikInput
                              name="CONTACT_US_EMAIL"
                              label="Contact Email"
                              disabled={formDisabled}
                            />
                          </div>
                          <div className="d-flex justify-content-between mt-3">
                            <div className="w-50 me-2">
                                <FormikInput
                                  name="CONTACT_US_LINKEDIN"
                                  label="LinkedIn"
                                  disabled={formDisabled}
                                />
                            </div>
                            <div className="w-50 ms-2">
                              <FormikInput
                                name="CONTACT_US_FB"
                                label="Facebook"
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="d-flex justify-content-between mt-3">
                            <div className="w-50 me-2">
                              <FormikInput
                                name="CONTACT_US_INSTAGRAM"
                                label="Instagram"
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="w-50 ms-2">
                              <FormikInput
                                name="CONTACT_US_TIKTOK"
                                label="Tiktok"
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="d-flex justify-content-between mt-3">
                            <div className="w-50 me-2">
                              <FormikInput
                                name="CONTACT_US_TWITTER"
                                label="Twitter"
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                        </div>
                      }
                    </div>

                    <label ref={widgetsRef} className="mt-2" htmlFor="">Widgets</label>  
                    <div
                      className="ps-3"
                      onFocus={() => handleSectionFocus(SECTION.WIDGETS)}>
                      {isLoading ?
                          <Skeleton height={355} />
                          :
                          <div className="mb-4">
                            <SortableList
                                items={filteredMobileWidgetService}
                                helperClass="mobile-services-item"
                                onSortEnd={onWidgetSortEnd}
                                onChange={toggleWidgetService}
                                axis="xy"
                                lockOffset="100%"
                                lockToContainerEdges
                                disableAutoscroll
                                disabled={formDisabled}
                            />
                          </div>
                      }
                    </div>

                    <label ref={featuresRef} className="mt-2" htmlFor="">Features</label>
                    <div
                      className="ps-3"
                      onFocus={() => handleSectionFocus(SECTION.FEATURES)}>
                      {(() => {
                        const mirrorMirror = filteredMobileServices.find(s => s.name === SERVICE_FEATURE_NAME.mirrorMirror);
                        return mirrorMirror ? (
                          <div className="mb-3" style={{pointerEvents: isSubmitting ? "none" : "auto"}}>
                            <div className="mobile-services-item d-flex align-items-center justify-content-between" style={{border: "1px solid #1976d2", borderRadius: 8, padding: "1rem", background: "#fff"}}>
                              <span>{SERVICE_FEATURE_NAME.mirrorMirror}</span>
                              <div className="d-flex align-items-center">
                                <input
                                  type="checkbox"
                                  name={mirrorMirror.id}
                                  checked={!!mirrorMirror.isEnabled}
                                  onChange={() => toggleService(mirrorMirror.id)}
                                  disabled={isSubmitting || formDisabled}
                                />
                              </div>
                            </div>
                          </div>
                        ) : null;
                      })()}
                    </div>
                    <label className="mt-2" htmlFor="">Navigation bar</label>
                    <div
                      className="ps-3"
                      onFocus={() => handleSectionFocus(SECTION.FEATURES)}>
                      {isLoading ?
                          <Skeleton height={355} />
                          :
                          (() => {
                            const rest = filteredMobileServices.filter(s => s.name !== SERVICE_FEATURE_NAME.mirrorMirror);
                            return (
                              <div>
                                <SortableList
                                  items={rest}
                                  helperClass="mobile-services-item"
                                  onSortEnd={onSortEnd}
                                  onChange={toggleService}
                                  axis="xy"
                                  lockOffset="100%"
                                  lockToContainerEdges
                                  disableAutoscroll
                                  disabled={formDisabled}
                                />
                              </div>
                            );
                          })()}
                    </div>

                    <div className="d-flex justify-content-end mt-5 gap-3">
                      <Button
                          color={BUTTON_COLORS.primary}
                          type="submit"
                          disabled={!!Object.keys(errors).length || isSubmitting || formDisabled}
                      >
                        Save changes
                      </Button>
                    </div>
                  </form>
              )
            }}
          </Formik>

        </section>

        <section className="text-center w-30 ps-5" style={{ position: "sticky", top: "64px", alignSelf: "flex-start", paddingTop: "1rem" }}>
          <Title
            title="Preview"
          />

          <div className="w-100 d-flex align-items-center justify-content-center">
            <MobilePreviewCarousel slides={mobileSlides} activeIndex={activeSlide} onChange={setActiveSlide} />
          </div>
        </section>
      </div>
    </div>

  </BaseLayoutWithCard>;
}